import { ChangeDetectorRef, Component, ElementRef, HostListener, inject, Input, ViewEncapsulation, Host, Optional } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { LoadingStateService } from '../../../providers/loading-state/loading-state.service';
import { CardComponent } from '../../card/card.component';

@Component({
    selector: 'tidy-button',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./button.component.scss'],
    template: `
    <div class="wrapper">
      <button
        expand="block"
        [ngClass]="{
          'mat-flat-button': hasATidyCardHost,
          'mat-raised-button': !hasATidyCardHost,
          'small-body-button': smallBody
        }"
        mat-button
        [disabled]="(disabled || ((loadingState.loading || actionLoading) && !disableLoadingStyle))"
        tappable
        (click)="executeAction()"
        [type]="type" block
        [attr.data-testid]="testId"
        >
        <span class="button-content" [class.loading]="actionLoading && !disableLoadingStyle">
          @if (image) {
            <tidy-image [src]="image" [width]="imageWidth" class="no-right-margin"></tidy-image>
          }
          @if (!smallText && !smallBody) {
            <tidy-text [largeBody]="text"></tidy-text>
          }
          @if (smallText) {
            <tidy-text [body]="text"></tidy-text>
          }
          @if (smallBody) {
            <tidy-text [smallBody]="text"></tidy-text>
          }
        </span>
        @if (actionLoading && !disableLoadingStyle) {
          <ion-spinner></ion-spinner>
        }
      </button>
    </div>
    `,
    standalone: false
})
export class ButtonComponent {

  @Input() image: string;
  @Input() imageWidth: string;
  @Input() text: string;
  @Input() type = 'submit';
  @Input() disabled = false;
  @Input() action = null;
  @Input() smallText = false;
  @Input() smallBody = false;
  @Input() disableLoadingStyle = false;
  @Input() testId: string;
  actionLoading: boolean;

  private readonly elementRef = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    public loadingState: LoadingStateService,
    private toastCtrl: ToastController,
    @Optional() @Host() public hasATidyCardHost: CardComponent
  ) { }

  @HostListener('click')
  onHostClick(): void {
    const isDisabled = this.disabled || (this.loadingState.loading && !this.disableLoadingStyle);
    if (isDisabled) {
      const el = this.elementRef.nativeElement;
      el.classList.remove('shake');
      void el.offsetWidth;
      el.classList.add('shake');
      setTimeout(() => el.classList.remove('shake'), 400);
    }
  }

  async executeAction() {
    // loadingState.loading turns true only after the overlay is created, so it cannot block a quick second click
    if (this.actionLoading || this.loadingState.loading || !this.action) {
      return;
    }

    this.actionLoading = true;
    try {
      this.loadingState.loading = true;
      await this.action();
    } catch (error) {
      await this.showError(error);
    } finally {
      this.loadingState.loading = false;
      this.actionLoading = false;
      // Inside an OnPush page nothing else re-renders the button, so it would stay disabled
      this.cdr.markForCheck();
    }
  }

  async showError(error) {
    const message = typeof error === 'string' ? error : (error?.message || error?.error?.message || 'An error occurred');
    await (await this.toastCtrl.create({ message, duration: 3000 })).present();
  }
}
